/**
 * @file 通用布局组件
 */
import { useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import {
  Web3Header,
  Web3Footer,
  web3FeTeamUseInit,
  userWalletStore,
} from '@web3fe/infra';
import { monitorInit, sensorsInit } from '@web3/base-tracking';
import { appName } from '@web3/base-utils';
import { Nested, DepositProvider } from '@web3/base-provider-v2';
import { useChangeLocale, useRem } from '@web3/base-hooks';
import { ToastContainer } from '@bfe/moly';
import cls from 'classnames';
import { NoSSR } from '../noSSR';
import { AppsFlyers } from '../appsFlyers';

import styles from './index.module.scss';

monitorInit();
sensorsInit({ userWalletStore });
web3FeTeamUseInit(appName);

interface Props {
  locale?: string;
  messages?: Record<string, string>;
  children: React.ReactNode;
  // rem配置，不传不会设置h5的rem
  remConfig?: {
    breakpointWidth: number;
    designWidth: number;
  };
  noSeoHead?: boolean;
  web3FooterProps?: {
    onlyCopyRight?: boolean;
  }; // Web3FooterProps;
  hasLoadAppsFlyers?: boolean;
  // 自定义LCP设置，默认开启
  useCustomLcp?: boolean;
}

export const Layout = (props: Props) => {
  const {
    locale = 'en',
    messages = {},
    remConfig,
    web3FooterProps = {},
    hasLoadAppsFlyers = true,
    children,
    noSeoHead = true,
    useCustomLcp = true,
  } = props;
  // 通过语言选择器切换后，不用刷新页面重新拉取语言
  useChangeLocale();
  useRem(remConfig);

  const providers = useMemo(() => {
    return [
      ({ children }) => (
        <IntlProvider
          messages={messages}
          locale={locale}
          onError={(err) => {
            if (!err?.message.match(/ed-ED/)) {
              /* eslint-disable jsx-control-statements/jsx-jcs-no-undef */
              console.warn(err?.message);
            }
          }}
        >
          {children}
        </IntlProvider>
      ),
      ({ children }) => <DepositProvider>{children}</DepositProvider>,
      DepositProvider,
    ];
  }, [locale, props.messages]);

  // 解决monitor mark两次问题，进而上报正确自定义LCP数值
  useEffect(() => {
    if (!useCustomLcp) return;
    document.body.click();
  }, [useCustomLcp]);

  return (
    <Nested components={providers}>
      <div className={styles.container}>
        {/* web3-dangerous-header别删，为了在App环境内隐藏head使用 */}
        <div className={cls(styles.head, 'web3-dangerous-header')}>
          <NoSSR>
            <Web3Header noSEO={noSeoHead} />
          </NoSSR>
        </div>
        <div className={styles.content} data-paint={useCustomLcp}>
          {children}
        </div>
        <NoSSR>
          <Web3Footer {...web3FooterProps} />
          <ToastContainer />
          {hasLoadAppsFlyers && <AppsFlyers remConfig={remConfig} />}
        </NoSSR>
      </div>
    </Nested>
  );
};
